import { Badge } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { ChainRunLink } from '@/types'
import { MessageSquare, Play } from 'lucide-react'
import { useId } from 'react'

interface Props {
  link: ChainRunLink
  mode: 'inputs' | 'manual'
  errorSummary: string | null
  blockedReason?: string
  onResume: () => void
}

export function ChainPauseBanner({ link, mode, errorSummary, blockedReason, onResume }: Props) {
  const reasonId = useId()
  const isManual = mode === 'manual'
  const Icon = isManual ? Play : MessageSquare
  const title = isManual ? 'Awaiting manual start' : 'Awaiting inputs'
  const actionLabel = isManual ? 'Start link' : 'Resume…'
  const actionAriaLabel = isManual ? 'Start link' : 'Supply missing inputs'

  return (
    <div className="rounded-lg border border-warning-foreground bg-warning p-4" data-testid="chain-pause-banner" role="alert">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-warning-foreground">
            <Icon className="h-4 w-4" />
            {title}
          </div>
          <Badge variant="outline" className="text-xs gap-1 bg-input-background text-warning-foreground border-warning-foreground">
            Link {link.position}
          </Badge>
        </div>

        <p className="text-sm text-warning-foreground">
          {isManual ? (
            <>&ldquo;{link.linkName}&rdquo; is configured to start manually. Start it when you are ready.</>
          ) : (
            <>
              &ldquo;{link.linkName}&rdquo; needs {link.missingInputs.join(', ')}. The chain verified inputs before
              running and paused instead of running with a blank &mdash; fill it in to continue.
            </>
          )}
        </p>

        {errorSummary && (
          <div>
            <p className="text-xs font-medium text-warning-foreground mb-1">Error summary</p>
            <p className="text-sm text-warning-foreground">{errorSummary}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {blockedReason && (
            <p id={reasonId} className="text-xs text-warning-foreground" data-testid="chain-pause-blocked-reason">
              {blockedReason}
            </p>
          )}
          <button
            className={cn(
              'flex items-center gap-1 text-sm font-medium text-warning-foreground',
              blockedReason && 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => { if (!blockedReason) onResume() }}
            aria-disabled={!!blockedReason}
            aria-describedby={blockedReason ? reasonId : undefined}
            aria-label={actionAriaLabel}
          >
            <Play className="h-4 w-4" /> {actionLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
